import React from 'react';
import type { AdminApiError } from '../../types';
import type {
  AdminDocumentPackageBundle,
  AdminDocumentPackageDetail,
  AdminDocumentSignerPlanEntry,
  AdminLegalProfile,
  AdminLegalProfileUpdate,
  AdminLegalSignatureEvidence,
  ETagged,
  LegalPackageCommand,
  LegalSignerCommand,
  SignedArtifactUploadInput,
} from '../../legalTypes';
import type {
  AdminDocumentConflict,
  AdminLegalProfileConflict,
} from '../../store/useSuperAdminStore';
import { AdminButton } from '../components';
import DocumentActionsPanel from './DocumentActionsPanel';
import {
  DocumentsAndVersions,
  GenerationStatus,
  PackageOverview,
  PackageTimeline,
} from './DocumentHistorySections';
import LegalProfileEditor from './LegalProfileEditor';
import SignatureEvidencePanel from './SignatureEvidencePanel';
import SignerPlanEditor from './SignerPlanEditor';

interface DocumentPackageDetailProps {
  detail: ETagged<AdminDocumentPackageDetail>;
  bundle: AdminDocumentPackageBundle;
  profile: ETagged<AdminLegalProfile>;
  conflict: AdminDocumentConflict | null;
  profileConflict: AdminLegalProfileConflict | null;
  evidence: AdminLegalSignatureEvidence[] | null;
  error: AdminApiError | null;
  partialError: string | null;
  busy: boolean;
  onSaveProfile: (input: AdminLegalProfileUpdate) => Promise<ETagged<AdminLegalProfile>>;
  onRefreshProfile: () => Promise<void>;
  onPackageCommand: (command: LegalPackageCommand) => Promise<AdminDocumentPackageDetail>;
  onRefreshConflict: () => Promise<void>;
  onConfigureSigners: (signers: AdminDocumentSignerPlanEntry[]) => Promise<AdminDocumentPackageDetail>;
  onSignerCommand: (signerId: string, command: LegalSignerCommand) => Promise<AdminDocumentPackageDetail>;
  onUpload: (signerId: string, input: SignedArtifactUploadInput) => Promise<AdminLegalSignatureEvidence>;
  onComplete: (signerId: string) => Promise<AdminDocumentPackageDetail>;
  onDownloadDocument: (documentId: string) => Promise<void>;
  onDownloadPackage: () => Promise<void>;
  onClose: () => void;
}

const DocumentConflictAlert: React.FC<{
  conflict: AdminDocumentConflict;
  onRefresh: () => Promise<void>;
}> = ({ conflict, onRefresh }) => (
  <div role="alert" aria-label="Document conflict" className="admin-legal-callout admin-legal-callout--danger">
    <strong>{conflict.error.message}</strong>
    <p>Your version: {conflict.local.version}. Server version: {conflict.current.version}.</p>
    <p>The local package remains unchanged until you compare and refresh.</p>
    <AdminButton onClick={() => void onRefresh()}>Refresh current package</AdminButton>
  </div>
);

const DocumentPackageDetail: React.FC<DocumentPackageDetailProps> = ({
  detail,
  bundle,
  profile,
  conflict,
  profileConflict,
  evidence,
  error,
  partialError,
  busy,
  onSaveProfile,
  onRefreshProfile,
  onPackageCommand,
  onRefreshConflict,
  onConfigureSigners,
  onSignerCommand,
  onUpload,
  onComplete,
  onDownloadDocument,
  onDownloadPackage,
  onClose,
}) => (
  <section className="admin-document-detail" aria-label="Selected document package">
    <header className="admin-document-detail__header">
      <div><span className="admin-legal-eyebrow">{profile.data.legalName}</span><h2>Package v{detail.data.sequence}</h2><p>{detail.data.purpose.replace(/_/g, ' ')} · ETag <span data-visual-mask>{detail.etag}</span></p></div>
      <AdminButton variant="ghost" onClick={onClose}>Close package</AdminButton>
    </header>
    {conflict && <DocumentConflictAlert conflict={conflict} onRefresh={onRefreshConflict} />}
    {partialError && <div role="alert" className="admin-legal-callout admin-legal-callout--warning"><strong>Some package evidence is unavailable.</strong><p>{partialError}</p></div>}
    {error && !conflict && error.code !== 'artifact_quarantined' && <div role="alert" aria-label="Document action failed" className="admin-legal-callout admin-legal-callout--danger"><strong>Document action failed.</strong><p>{error.message}</p></div>}
    <div className="admin-document-detail__grid">
      <PackageOverview detail={detail.data} />
      <LegalProfileEditor profile={profile} conflict={profileConflict} saving={busy} onSave={onSaveProfile} onRefresh={onRefreshProfile} />
      <DocumentsAndVersions detail={detail.data} bundle={bundle} onDownloadDocument={onDownloadDocument} onDownloadPackage={onDownloadPackage} />
      <GenerationStatus bundle={bundle} />
      <section className="admin-legal-card" aria-labelledby="recipient-plan-title"><div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Frozen at delivery</span><h3 id="recipient-plan-title">Recipient plan</h3></div><SignerPlanEditor detail={detail.data} busy={busy} onSave={onConfigureSigners} /></div><p>Configure and verify recipients before sending. Once delivery starts, identities and order are immutable.</p></section>
      <DocumentActionsPanel detail={detail.data} companyName={profile.data.legalName} busy={busy} onPackageCommand={onPackageCommand} />
      <SignatureEvidencePanel detail={detail.data} companyName={profile.data.legalName} busy={busy} error={error} evidence={evidence} onUpload={onUpload} onComplete={onComplete} onSignerCommand={onSignerCommand} />
      <PackageTimeline bundle={bundle} />
    </div>
  </section>
);

export default DocumentPackageDetail;
